import { Alert, Paper, Typography } from "@mui/material";
import { FormikDimensionedNumberInput } from "common/components/FormikDimensionedNumberInput";
import { TractorCalibration } from "protos/atk/calibration";
import { TypedPathTo } from "portal/utils/typedPath";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import React from "react";

interface GpsOffsetCalibrationSubformProps<
  TBasePath extends TypedPathTo<TractorCalibration>
> {
  basePath: TBasePath;
  readOnly: boolean;
}

export const GpsOffsetCalibrationSubform = <
  TBasePath extends TypedPathTo<TractorCalibration>
>({
  basePath,
  readOnly,
}: GpsOffsetCalibrationSubformProps<TBasePath>): JSX.Element => {
  const { t } = useTranslation();
  const { measurementSystem } = useSelf();

  return (
    <Paper className="grid grid-cols-2 gap-4 p-4">
      <Typography className="col-span-full pb-4 font-bold">
        {t("views.autotractor.equipment.tractors.calibration.gps.title")}
      </Typography>
      <FormikDimensionedNumberInput
        name={basePath.gpsPosXMm.$path}
        label={t(
          "views.autotractor.equipment.tractors.calibration.gps.xOffset"
        )}
        dimensionedNumberInputProps={{
          storedUnit: "mm",
          localSystem: measurementSystem,
          displayUnits: {
            imperial: "in",
            metric: "cm",
          },
          disabled: readOnly,
        }}
      />
      <Alert severity="info">
        {t("views.autotractor.equipment.tractors.calibration.gps.xOffsetNote")}
      </Alert>
      <FormikDimensionedNumberInput
        name={basePath.gpsPosYMm.$path}
        label={t(
          "views.autotractor.equipment.tractors.calibration.gps.yOffset"
        )}
        dimensionedNumberInputProps={{
          storedUnit: "mm",
          localSystem: measurementSystem,
          displayUnits: {
            imperial: "in",
            metric: "cm",
          },
          disabled: readOnly,
        }}
      />
      <Alert severity="info">
        {t("views.autotractor.equipment.tractors.calibration.gps.yOffsetNote")}
      </Alert>
      <FormikDimensionedNumberInput
        name={basePath.gpsHeadingOffsetDeg.$path}
        label={t(
          "views.autotractor.equipment.tractors.calibration.gps.headingOffset"
        )}
        dimensionedNumberInputProps={{
          storedUnit: "deg",
          localSystem: measurementSystem,
          displayUnits: {
            imperial: "deg",
            metric: "deg",
          },
          disabled: readOnly,
        }}
      />
      <Alert severity="info">
        {t(
          "views.autotractor.equipment.tractors.calibration.gps.headingOffsetNote"
        )}
      </Alert>
    </Paper>
  );
};
